/**
 * VD Hero carousel — crossfade slideshow for sections/vd-hero.liquid.
 *
 * Only loaded when the section renders more than one "slide" block (see the
 * `{%- if slide_count > 1 -%}` guard around the <script> tag in
 * vd-hero.liquid), so a single-slide hero never pays for this file.
 *
 * Behavior:
 * - Crossfade between `.vd-hero__slide` layers via opacity (CSS handles the
 *   800ms transition; this file only toggles `.is-active`).
 * - Autoplay every 6.5s, but only when there's more than one slide, the
 *   visitor hasn't requested reduced motion, and the tab is visible —
 *   paused on `visibilitychange` to hidden, resumed on visible.
 * - Per-slide video: play the active slide's video (if it has one) and
 *   pause every other slide's video, so at most one video decodes/plays
 *   at a time.
 * - Copy crossfade: eyebrow/heading/sub only fade+swap when the incoming
 *   slide's resolved text actually differs from what's currently shown —
 *   a slide with no copy override resolves to the same section-default
 *   text, so back-to-back slides that share the same resolved copy don't
 *   flash/refade for no reason.
 * - Dots: minimal 44px-target buttons, bottom-right, keyboard-operable
 *   (native <button>, Enter/Space activates).
 * - Touch: horizontal swipe on the media area advances/retreats a slide.
 *
 * Vanilla JS, no dependencies.
 */
(function () {
  'use strict';

  var AUTOPLAY_INTERVAL_MS = 6500;
  var TEXT_FADE_MS = 220;
  var SWIPE_THRESHOLD_PX = 40;

  function initCarousel(section) {
    var slides = Array.prototype.slice.call(section.querySelectorAll('.vd-hero__slide'));
    var dots = Array.prototype.slice.call(section.querySelectorAll('.vd-hero__dot'));
    var media = section.querySelector('.vd-hero__media');
    var eyebrowEl = section.querySelector('.vd-hero__eyebrow');
    var headingEl = section.querySelector('.vd-hero__heading');
    var subEl = section.querySelector('.vd-hero__sub');

    if (slides.length < 2) return;

    var reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    var currentIndex = slides.findIndex(function (slide) {
      return slide.classList.contains('is-active');
    });
    if (currentIndex < 0) currentIndex = 0;

    var autoplayTimer = null;

    function reducedMotion() {
      return reducedMotionQuery.matches;
    }

    function slideVideo(slide) {
      return slide.querySelector('.vd-hero__video');
    }

    function updateTextField(el, newText) {
      if (!el) return;
      var currentText = el.textContent.trim();
      var nextText = newText || '';

      if (currentText === nextText) return;

      if (reducedMotion()) {
        el.textContent = nextText;
        return;
      }

      el.classList.add('vd-hero__text--fade');
      window.setTimeout(function () {
        el.textContent = nextText;
        el.classList.remove('vd-hero__text--fade');
      }, TEXT_FADE_MS);
    }

    function activate(index) {
      if (index === currentIndex || !slides[index]) return;

      var previousSlide = slides[currentIndex];
      var nextSlide = slides[index];

      previousSlide.classList.remove('is-active');
      nextSlide.classList.add('is-active');

      var previousVideo = slideVideo(previousSlide);
      if (previousVideo) {
        previousVideo.pause();
      }

      var nextVideo = slideVideo(nextSlide);
      if (nextVideo && !reducedMotion()) {
        nextVideo.currentTime = 0;
        // play() returns a promise that rejects if the browser blocks
        // autoplay for this element; swallow that rather than throwing an
        // unhandled rejection into the console.
        var playResult = nextVideo.play();
        if (playResult && typeof playResult.catch === 'function') {
          playResult.catch(function () {});
        }
      }

      dots.forEach(function (dot, dotIndex) {
        var isActive = dotIndex === index;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      updateTextField(eyebrowEl, nextSlide.dataset.eyebrow);
      updateTextField(headingEl, nextSlide.dataset.heading);
      updateTextField(subEl, nextSlide.dataset.sub);

      currentIndex = index;
    }

    function goToNext() {
      activate((currentIndex + 1) % slides.length);
    }

    function goToPrevious() {
      activate((currentIndex - 1 + slides.length) % slides.length);
    }

    function stopAutoplay() {
      if (autoplayTimer) {
        window.clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    }

    function startAutoplay() {
      stopAutoplay();
      if (slides.length < 2 || reducedMotion() || document.visibilityState !== 'visible') {
        return;
      }
      autoplayTimer = window.setInterval(goToNext, AUTOPLAY_INTERVAL_MS);
    }

    function restartAutoplay() {
      // Manual navigation (dot click, swipe, keyboard) resets the
      // countdown so the slide the visitor just chose gets its full
      // dwell time before auto-advancing.
      startAutoplay();
    }

    dots.forEach(function (dot, dotIndex) {
      dot.addEventListener('click', function () {
        activate(dotIndex);
        restartAutoplay();
      });
    });

    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') {
        stopAutoplay();
      } else {
        startAutoplay();
      }
    });

    reducedMotionQuery.addEventListener('change', function () {
      startAutoplay();
    });

    // Touch swipe on the media layer.
    if (media) {
      var touchStartX = 0;
      var touchStartY = 0;
      var touchActive = false;

      media.addEventListener(
        'touchstart',
        function (event) {
          if (event.touches.length !== 1) return;
          touchActive = true;
          touchStartX = event.touches[0].clientX;
          touchStartY = event.touches[0].clientY;
        },
        { passive: true }
      );

      media.addEventListener(
        'touchend',
        function (event) {
          if (!touchActive) return;
          touchActive = false;

          var touch = event.changedTouches[0];
          var deltaX = touch.clientX - touchStartX;
          var deltaY = touch.clientY - touchStartY;

          if (Math.abs(deltaX) < SWIPE_THRESHOLD_PX || Math.abs(deltaX) < Math.abs(deltaY)) {
            return;
          }

          if (deltaX < 0) {
            goToNext();
          } else {
            goToPrevious();
          }
          restartAutoplay();
        },
        { passive: true }
      );
    }

    startAutoplay();
  }

  function init() {
    var sections = document.querySelectorAll('[data-vd-hero-carousel]');
    sections.forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
